import type { XchainStage } from "@decibeltrade/utils";

// Type-only import: the runtime side of the card pulls in icon modules, which unit tests
// running this pure mapping have no reason to load.
import type { TransferStep } from "./transfer-progress-card";

/**
 * Which progress step a 1Click stage corresponds to, or `null` when the deposit should not
 * be shown at all.
 *
 * Only `delivered` and `failed` produce a card: a delivery comes into existence already settled,
 * so there is nothing upstream that represents money still on its way. `pending` is not the
 * liveness signal it is on the withdrawal side — `toDepositStage` funnels every status it cannot
 * map into it, so a card built from one would sit at a step that is neither complete nor
 * actionable, with no later transition to wait for.
 */
export function xchainStepForStage(stage: XchainStage): TransferStep | null {
  switch (stage) {
    case "waiting":
    case "pending":
      return null;
    case "delivered":
      return "available";
    case "failed":
      return "transfer-failed";
  }
}

/**
 * The same mapping for a withdrawal, which differs at both ends.
 *
 * `waiting` is a step rather than a skip: the card is created by the send itself, so a record
 * 1Click has not picked up yet is a transfer in its first seconds, not an address that may never
 * be funded. `delivered` is `completed` rather than `available`: the funds left the exchange, so
 * there is no follow-up move into collateral to offer and delivery is terminal.
 */
export function xchainWithdrawStepForStage(stage: XchainStage): TransferStep {
  switch (stage) {
    case "waiting":
      return "confirming";
    case "pending":
      return "bridging";
    case "delivered":
      return "completed";
    case "failed":
      return "transfer-failed";
  }
}

function isAmount(value: string | null | undefined): value is string {
  return !!value && Number.isFinite(Number(value));
}

interface DepositAmounts {
  amountOutFormatted: string | null;
}

/**
 * How much the delivery put in the user's primary subaccount, exactly as the server reported it.
 *
 * Only the destination-side figure exists — a delivery says what arrived, not what was sent, and
 * what arrived is what the follow-up move into collateral has to shift anyway.
 *
 * Kept as the string rather than a number because the move into collateral is a `u64`: floating
 * point cannot represent most of these values, and the ones it rounds down cost the user a base
 * unit they never get back. See `actions/sweep-amount.ts`.
 */
export function xchainDepositAmountFormatted(deposit: DepositAmounts): string | null {
  return isAmount(deposit.amountOutFormatted) ? deposit.amountOutFormatted : null;
}

/**
 * The same figure as a number, for display and analytics only.
 *
 * Derived from the string above rather than picking a field itself, so the two can never end up
 * naming different sides of the swap.
 */
export function xchainDepositAmount(deposit: DepositAmounts): number {
  return Number(xchainDepositAmountFormatted(deposit) ?? 0);
}

interface FeeAmounts {
  withdrawFeeFormatted: string | null;
}

interface FeeUsdAmounts {
  withdrawFeeUsd?: string | null;
}

/**
 * What 1Click actually charged, exactly as reported, for a settled deposit or a withdrawal quote.
 *
 * `undefined` rather than `null` when it has not settled yet, so a card can spread this straight
 * into its props and never render a row for it (`null` would still be a key with a value).
 * `"0"` is preserved: 1Click sometimes settles at zero fee, which is a fact worth showing, not the
 * same as "we don't know" — never derived by subtracting an input amount from an output one, which
 * would round through the exact figure both already are.
 */
export function xchainFeeFormatted(source: FeeAmounts): string | undefined {
  return isAmount(source.withdrawFeeFormatted) ? source.withdrawFeeFormatted : undefined;
}

/**
 * The USD value of {@link xchainFeeFormatted}, when the server supplied one.
 *
 * Deposits carry this; withdrawals do not, so the field is optional on the input rather than
 * nullable — there is nothing to distinguish "not yet settled" from "this record never has one".
 */
export function xchainFeeUsd(source: FeeUsdAmounts): string | undefined {
  return isAmount(source.withdrawFeeUsd ?? null) ? (source.withdrawFeeUsd ?? undefined) : undefined;
}
